import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { InvoiceLineItem, CatalogProduct } from '@techgitter/contracts';
import { SearchAhead } from '../dropdown';
import { getProducts } from '../../services/productCacheService';
import './InvoiceLineItemEditor.css';

interface InvoiceLineItemEditorProps {
  lineItems: InvoiceLineItem[];
  // RS stores line item `price` GST-inclusive, so totals must come from the
  // backend (cr001_subtotal/tax/total). Recomputing locally as price*qty plus
  // 10% would double-count GST.
  subtotal: number;
  tax: number;
  total: number;
  readOnly: boolean;
  onAdd: (item: {
    name: string;
    description: string;
    quantity: number;
    price: number;
    discountPercent: number;
    productId?: number;
  }) => Promise<void>;
  onUpdate: (lineItemId: number, updates: {
    description?: string;
    quantity?: number;
    price?: number;
    discountPercent?: number;
  }) => Promise<void>;
  onDelete: (lineItemId: number) => Promise<void>;
}

// Light header band matching the shared DataTable kit (§9): 12px/500 #888
// uppercase on the row's #fafbfd band, hairline bottom divider. Horizontal
// padding is left to the MUI default so header columns stay aligned with the
// body cells.
const headerCellSx = {
  color: '#888888',
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  borderBottom: '0.5px solid #e2eaf3',
  py: 1,
};

const bodyCellSx = {
  fontSize: 14,
  py: 0.75,
};

const inputSx = {
  '& .MuiInput-input': { fontSize: 14 },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(value);
}

export const InvoiceLineItemEditor: React.FC<InvoiceLineItemEditorProps> = ({
  lineItems,
  subtotal,
  tax,
  total,
  readOnly,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [addingRow, setAddingRow] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<number | 'new'>>(new Set());

  // Draft state for the "add new" row
  const [newProduct, setNewProduct] = useState<CatalogProduct | null>(null);
  const [newDescription, setNewDescription] = useState('');
  const [newQty, setNewQty] = useState(1);
  const [newPrice, setNewPrice] = useState(0);
  const [productQuery, setProductQuery] = useState('');

  useEffect(() => {
    getProducts()
      .then((result) => setProducts(result.items.filter((p) => !p.disabled)))
      .catch(() => {});
  }, []);

  // Deduped, sorted catalog + SKU lookup backing the SearchAhead product picker.
  const productList = useMemo(() => {
    const bySku = new Map<string, CatalogProduct>();
    for (const p of products) bySku.set(p.sku, p);
    return [...bySku.values()].sort((a, b) => a.sku.localeCompare(b.sku));
  }, [products]);

  const productBySku = useMemo(() => {
    const m = new Map<string, CatalogProduct>();
    for (const p of productList) m.set(p.sku, p);
    return m;
  }, [productList]);

  const productResults = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    const base = q
      ? productList.filter(
          (p) =>
            p.sku.toLowerCase().includes(q) ||
            p.displayName.toLowerCase().includes(q),
        )
      : productList;
    return base.slice(0, 50).map((p) => ({ code: p.sku, description: p.displayName }));
  }, [productList, productQuery]);

  const resetNewRow = () => {
    setNewProduct(null);
    setNewDescription('');
    setNewQty(1);
    setNewPrice(0);
    setProductQuery('');
    setAddingRow(false);
  };

  const handleProductSelect = (product: CatalogProduct | null) => {
    setNewProduct(product);
    if (product) {
      setNewDescription(product.displayName);
      setNewPrice(product.unitCost);
    }
  };

  const handleSaveNewRow = async () => {
    if (!newProduct) return;
    setSavingIds((prev) => new Set(prev).add('new'));
    try {
      await onAdd({
        name: newProduct.sku,
        description: newDescription,
        quantity: newQty,
        price: newPrice,
        discountPercent: 0,
        productId: newProduct.productId ? Number(newProduct.productId) : undefined,
      });
      resetNewRow();
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete('new');
        return next;
      });
    }
  };

  const handleUpdateField = async (
    lineItemId: number,
    field: 'description' | 'quantity' | 'price' | 'discountPercent',
    value: string | number,
  ) => {
    setSavingIds((prev) => new Set(prev).add(lineItemId));
    try {
      await onUpdate(lineItemId, { [field]: value });
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(lineItemId);
        return next;
      });
    }
  };

  const handleDelete = async (lineItemId: number) => {
    setSavingIds((prev) => new Set(prev).add(lineItemId));
    try {
      await onDelete(lineItemId);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(lineItemId);
        return next;
      });
    }
  };

  // Per-row total is qty * price * (1 - discount/100). RS stores price
  // GST-inclusive, so this is the line's inc-GST contribution. The
  // overall subtotal/tax/total at the bottom come from the backend.
  const computeLineTotal = (qty: number, price: number, discount: number): number =>
    qty * price * (1 - discount / 100);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight="bold">Line Items</Typography>
        {!readOnly && !addingRow && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingRow(true)}>
            Add Item
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafbfd' }}>
              <TableCell sx={{ ...headerCellSx, width: '22%' }}>Product</TableCell>
              <TableCell sx={headerCellSx}>Description</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 91 }}>Qty</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 90 }}>Price</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 100, textAlign: 'right' }}>Total</TableCell>
              {!readOnly && <TableCell sx={{ ...headerCellSx, width: 40 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.length === 0 && !addingRow && (
              <TableRow>
                <TableCell colSpan={readOnly ? 5 : 6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No line items
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {lineItems.map((li) => {
              const isSaving = savingIds.has(li.id);
              const lineTotal = computeLineTotal(li.quantity, li.price, li.discountPercent);

              return (
                <TableRow key={li.id} sx={{ '& .MuiTableCell-root': bodyCellSx }}>
                  <TableCell>{li.name}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {readOnly ? (
                      li.description
                    ) : (
                      <TextField
                        defaultValue={li.description}
                        onBlur={(e) => {
                          if (e.target.value !== li.description) {
                            handleUpdateField(li.id, 'description', e.target.value);
                          }
                        }}
                        size="small"
                        fullWidth
                        multiline
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={inputSx}
                        disabled={isSaving}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      li.quantity
                    ) : (
                      <TextField
                        defaultValue={li.quantity}
                        onBlur={(e) => {
                          const val = Number(e.target.value) || 0;
                          if (val !== li.quantity) {
                            handleUpdateField(li.id, 'quantity', val);
                          }
                        }}
                        size="small"
                        type="number"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        inputProps={{ min: 0, step: 1 }}
                        sx={inputSx}
                        disabled={isSaving}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      formatCurrency(li.price)
                    ) : (
                      <TextField
                        defaultValue={li.price}
                        onBlur={(e) => {
                          const val = Number(e.target.value) || 0;
                          if (val !== li.price) {
                            handleUpdateField(li.id, 'price', val);
                          }
                        }}
                        size="small"
                        type="number"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        inputProps={{ step: 0.01 }}
                        sx={inputSx}
                        disabled={isSaving}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {isSaving ? (
                      <CircularProgress size={14} />
                    ) : (
                      formatCurrency(lineTotal)
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(li.id)}
                        disabled={isSaving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {/* Add new row */}
            {addingRow && (
              <TableRow sx={{ '& .MuiTableCell-root': bodyCellSx, backgroundColor: 'action.hover' }}>
                <TableCell>
                  <div className="invoice-li-product">
                    <SearchAhead
                      value={productQuery}
                      results={productResults}
                      label=""
                      placeholder="Product"
                      autoFocus
                      secondaryText={newProduct ? newProduct.displayName : undefined}
                      onSearch={setProductQuery}
                      onSelect={(r) => {
                        const product = productBySku.get(r.code) ?? null;
                        handleProductSelect(product);
                        setProductQuery(product ? product.sku : r.code);
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  <TextField
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    placeholder="Description"
                    sx={inputSx}
                    disabled={savingIds.has('new')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={newQty}
                    onChange={(e) => setNewQty(Number(e.target.value) || 0)}
                    size="small"
                    type="number"
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ min: 0, step: 1 }}
                    sx={inputSx}
                    disabled={savingIds.has('new')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={newPrice}
                    onChange={(e) => setNewPrice(Number(e.target.value) || 0)}
                    size="small"
                    type="number"
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ step: 0.01 }}
                    sx={inputSx}
                    disabled={savingIds.has('new')}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  {savingIds.has('new') ? (
                    <CircularProgress size={14} />
                  ) : (
                    formatCurrency(computeLineTotal(newQty, newPrice, 0))
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveNewRow}
                      disabled={!newProduct || savingIds.has('new')}
                      sx={{ minWidth: 0, px: 1, fontSize: 13 }}
                    >
                      Add
                    </Button>
                    <IconButton size="small" onClick={resetNewRow} disabled={savingIds.has('new')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 4 }}>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary">Subtotal</Typography>
          <Typography variant="body2" color="text.secondary">GST (10%)</Typography>
          <Typography variant="body2" fontWeight="bold">Total</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
          <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
          <Typography variant="body2">{formatCurrency(tax)}</Typography>
          <Typography variant="body2" fontWeight="bold">{formatCurrency(total)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};
